export type RGB = [number, number, number]

export type COLOR_FORMAT = 'RGB' | 'HEX' | 'HSL'

export const rgbToXY = (rgb: RGB, hue: RGB) => {
  const high = hue.findIndex((h) => h === 255)
  const low = hue.findIndex((h) => h === 0)
  const y = 1 - rgb[high] / 255 || 0
  const x = 1 - rgb[low] / rgb.reduce((a, b) => Math.max(a, b), 0) || 0
  return { x, y }
}

export const xyToRgb = (x: number, y: number, hue: RGB) =>
  hue.map((v) => (v + (255 - v) * (1 - x)) * (1 - y))

export const rgbToHue = (rgb: RGB): RGB => {
  const [r, g, b] = rgb

  if (r === g && g === b) {
    return [255, 0, 0]
  }

  let hr = r
  let hg = g
  let hb = b

  if (r >= g && r >= b) {
    hr = 255
  } else if (r <= g && r <= b) {
    hr = 0
  }

  if (g >= r && g >= b) {
    hg = 255
  } else if (g <= r && g <= b) {
    hg = 0
  }

  if (b >= r && b >= g) {
    hb = 255
  } else if (b <= r && b <= g) {
    hb = 0
  }

  return [hr, hg, hb]
}

export const toHex = (n: number) => Number(n).toString(16).padStart(2, '0')

export const minmax = (min: number, n: number, max: number): number =>
  Math.min(max, Math.max(min, n))

export const rgbaToArr = (str: string) => {
  const [, r, g, b, a] = str.split(/, |,|\(|\)/)
  // @ts-ignore
  return [Math.round(r), Math.round(g), Math.round(b), Number(a || 1)]
}

export const hexToRGBA = (hex: string, alpha = 1) => {
  var r = parseInt(hex.slice(1, 3), 16)
  var g = parseInt(hex.slice(3, 5), 16)
  var b = parseInt(hex.slice(5, 7), 16)

  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')'
}

export const rgbaToHex = (rgba: string): string => {
  // Extract RGBA values using existing rgbaToArr function
  const [r, g, b, a] = rgbaToArr(rgba)

  // Convert to hex and ensure 2 digits
  const rHex = toHex(r)
  const gHex = toHex(g)
  const bHex = toHex(b)

  // Convert alpha to hex (multiply by 255 to convert from 0-1 to 0-255)
  const aHex = toHex(Math.round(a * 255))

  // Return 8-digit hex if alpha is less than 1, otherwise return 6-digit hex
  return a < 1
    ? `#${rHex}${gHex}${bHex}${aHex}`.toUpperCase()
    : `#${rHex}${gHex}${bHex}`.toUpperCase()
}

export const rgbToHSL = ([r, g, b]: RGB): [number, number, number] => {
  // Convert RGB to 0-1 range
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)

    switch (max) {
      case r:
        h = (g - b) / delta + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / delta + 2
        break
      case b:
        h = (r - g) / delta + 4
        break
    }

    h *= 60
  }

  // Convert saturation and lightness to percentages
  s = Math.round(s * 100)
  l = Math.round(l * 100)
  h = Math.round(h)

  return [h, s, l]
}

export const rgbaToHsla = (rgba: string): string => {
  const [r, g, b, a] = rgbaToArr(rgba)
  const hsl = rgbToHSL([r, g, b])
  return `hsla(${hsl[0]}deg, ${hsl[1]}%, ${hsl[2]}%, ${a})`
}

export const hslToRGB = ([h, s, l]: [number, number, number]): RGB => {
  // Convert S,L to decimal
  s /= 100
  l /= 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r = 0
  let g = 0
  let b = 0

  if (0 <= h && h < 60) {
    r = c
    g = x
    b = 0
  } else if (60 <= h && h < 120) {
    r = x
    g = c
    b = 0
  } else if (120 <= h && h < 180) {
    r = 0
    g = c
    b = x
  } else if (180 <= h && h < 240) {
    r = 0
    g = x
    b = c
  } else if (240 <= h && h < 300) {
    r = x
    g = 0
    b = c
  } else if (300 <= h && h < 360) {
    r = c
    g = 0
    b = x
  }

  // Convert to 0-255 range
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ]
}

export const hslToRGBA = (hsl: string): string => {
  // Extract HSL and alpha values, handling both hsl and hsla formats
  const matches = hsl.match(
    /hsla?\(([\d.]+)(?:deg)?,\s*(\d+)%,\s*(\d+)%(?:,\s*([\d.]+))?\)/,
  )
  if (!matches) return 'rgba(0, 0, 0, 1)'

  const [, h, s, l, a = '1'] = matches
  const rgb = hslToRGB([Number(h), Number(s), Number(l)])

  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`
}

export const hexToHSL = (hex: string): string => {
  const rgba = hexToRGBA(hex)
  return rgbaToHsla(rgba)
}

export const isHex = (value: string): boolean => {
  if (!value) return false

  // Test for:
  // #RGB (3 digits)
  // #RGBA (4 digits)
  // #RRGGBB (6 digits)
  // #RRGGBBAA (8 digits)
  return /^#([A-Fa-f0-9]{8}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{3,4})$/.test(value)
}

export const isValidRGBA = (value: string): boolean => {
  if (!value) return false

  // Test for:
  // rgba(R,G,B,A)
  return /^rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*\d?(\.\d+)?\)$/.test(value)
}

export const isValidRGB = (value: string): boolean => {
  if (!value) return false
  // Test for:
  // rgb(R,G,B)
  return /^rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)$/.test(value)
}

export const isValidHSLA = (value: string): boolean => {
  if (!value) return false

  // Test for:
  // hsla(H,S%,L%,A)
  return /^hsla?\(((\d{1,3})(deg)?,\s*)(\d{1,3})%,\s*(\d{1,3})%(,\s*(\d?(\.\d+)?))?\)$/.test(
    value,
  )
}
